import { createAsync } from '@solidjs/router';
import { For, Show, Suspense } from 'solid-js';
import { Button, Link, Section, Heading, BreadcrumbItem, Breadcrumbs, ReservationCard } from '@troon/ui';
import { Title } from '@solidjs/meta';
import { IconCalendar } from '@troon/icons/calendar';
import { dayTimeToDate } from '@troon/dayjs';
import { gql, ReservationState } from '../../graphql';
import { cachedQuery } from '../../graphql/cached-get';
import type { RouteDefinition } from '@solidjs/router';

export default function Reservations() {
	const data = createAsync(() => getReservations({}), { deferStream: true });
	return (
		<>
			<Title>Reservations | My Account | Golf with Access</Title>

			<Breadcrumbs omitHome>
				<BreadcrumbItem href="/account">Account</BreadcrumbItem>
				<BreadcrumbItem href="/account/reservations">Reservations</BreadcrumbItem>
			</Breadcrumbs>

			<Heading as="h1">Reservations</Heading>

			<Section>
				<Heading as="h2" size="h4">
					Upcoming
				</Heading>

				<ul class="flex flex-col gap-y-8">
					<Suspense>
						<Show when={data()}>
							<For
								each={data()?.reservations.filter(
									(res) =>
										(res.state === ReservationState.Active || res.state === ReservationState.Pending) &&
										dayTimeToDate(res.dayTime).valueOf() > Date.now(),
								)}
								fallback={
									<li class="flex flex-col items-center gap-6 rounded-lg border border-neutral p-4 md:p-8">
										<IconCalendar class="size-20 text-brand md:size-24" />
										<p class="flex flex-col text-center">
											<span class="text-xl font-semibold">You have no upcoming reservations.</span>
											<span>When you book a tee time, you’ll find it here.</span>
										</p>
										<Button as={Link} href="/" appearance="secondary">
											Book a Tee Time
										</Button>
									</li>
								}
							>
								{(res) => (
									<li>
										<ReservationCard
											date={dayTimeToDate(res.dayTime)}
											facilityName={res.facility.name}
											courseName={res.course.name}
											holes={res.holeCount}
											players={res.playerCount}
											state={res.state}
											includesCart={res.includesCart}
											href={`/reservation/${res.id}`}
											image={res.facility.metadata?.hero?.url}
											action="Details"
										/>
									</li>
								)}
							</For>
						</Show>
					</Suspense>
				</ul>
			</Section>

			<Suspense>
				<Show
					when={data()?.reservations.filter(
						(res) =>
							(res.state !== ReservationState.Active && res.state !== ReservationState.Pending) ||
							dayTimeToDate(res.dayTime).valueOf() < Date.now(),
					)}
				>
					{(pastReservations) => (
						<Section>
							<Heading as="h2" size="h4">
								Past
							</Heading>
							<ul class="flex flex-col gap-y-8">
								<For each={pastReservations()} fallback={<li>No previous reservations</li>}>
									{(res) => (
										<li>
											<ReservationCard
												date={dayTimeToDate(res.dayTime)}
												facilityName={res.facility.name}
												courseName={res.course.name}
												holes={res.holeCount}
												players={res.playerCount}
												state={res.state}
												includesCart={res.includesCart}
												href={`/reservation/${res.id}`}
												image={res.facility.metadata?.hero?.url}
												action="Details"
											/>
										</li>
									)}
								</For>
							</ul>
						</Section>
					)}
				</Show>
			</Suspense>
		</>
	);
}

export const route = {
	preload: () => getReservations({}),
} satisfies RouteDefinition;

const reservationsQuery = gql(`
query allReservations {
	reservations: allReservations(sortOrder: DESC) {
		id
		dayTime { day { year, month, day }, time { hour, minute } }
		includesCart
		playerCount
		holeCount
		state
		facility {
			name
			metadata { hero { url } }
		}
		course { name }
	}
}`);

const getReservations = cachedQuery(reservationsQuery);
